import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription, catchError, finalize, of } from 'rxjs';
import {
  DatePickerComponent,
  FluidCapacityCarryState,
  FluidCapacityHourComponent,
  FluidCapacityHourSlot,
  PageContextGuardComponent,
} from '@clinic-platform/ui-components';

import { AuthService } from '../../../core/auth/auth.service';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { LoadingBlockComponent } from '../../../shared/ui/loading-block/loading-block.component';
import { EmptyStateComponent } from '../../../shared/ui/empty-state/empty-state.component';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { SlotsService } from '../../slots/data-access/slots.service';
import {
  SlotCapacityIntelligenceWindowDto,
  SlotCapacityReasonCode,
  SlotCapacitySignal,
} from '../../slots/data-access/slots.models';

interface CapacityStageHour {
  id: string;
  hourLabel: string;
  slots: FluidCapacityHourSlot[];
  carryState: FluidCapacityCarryState;
}

@Component({
  selector: 'app-appointment-capacity-stage-page',
  standalone: true,
  imports: [
    FormsModule,
    PageHeaderComponent,
    LoadingBlockComponent,
    EmptyStateComponent,
    CompanyContextRequiredComponent,
    PageContextGuardComponent,
    DatePickerComponent,
    FluidCapacityHourComponent,
  ],
  template: `
    <app-page-header
      title="Capacity stage"
      subtitle="Real capacity-intelligence read model rendered through the current compact baseline"
    />

    <lib-page-context-guard [blocked]="companyContextMissing">
      <app-company-context-required />
    </lib-page-context-guard>

    @if (!companyContextMissing) {
      <section class="capacity-stage">
        <div class="capacity-stage__toolbar">
          <lib-date-picker
            label="Date"
            inputId="capacity-stage-date"
            [value]="selectedDate"
            (valueChange)="onDateChanged($event)"
          />

          <label class="capacity-stage__field">
            <span>Clinic</span>
            <select
              [ngModel]="selectedClinicId"
              (ngModelChange)="onClinicChanged($event)"
            >
              <option value="">Select clinic</option>
              @for (clinic of clinicOptions; track clinic.id) {
                <option [value]="clinic.id">{{ clinic.label }}</option>
              }
            </select>
          </label>

          <button
            type="button"
            class="capacity-stage__refresh"
            [disabled]="isLoading || !selectedClinicId"
            (click)="loadCapacityStage()"
          >
            Refresh
          </button>
        </div>

        @if (isLoading) {
          <app-loading-block text="Loading capacity stage..." />
        } @else if (errorText) {
          <app-empty-state
            title="Failed to load capacity stage"
            [description]="errorText"
          />
        } @else if (!selectedClinicId) {
          <app-empty-state
            title="Clinic is required"
            description="Select a clinic to load capacity intelligence."
          />
        } @else if (hours.length === 0) {
          <app-empty-state
            title="No capacity windows"
            description="No slot windows were returned for the selected clinic and date."
          />
        } @else {
          <div class="capacity-stage__day">
            <div class="capacity-stage__day-head">
              <div>
                <h3>{{ selectedDate }}</h3>
                <p>Business success, bottleneck side, and compact diagnosis from backend read-model data.</p>
              </div>
            </div>

            <div class="capacity-stage__hours">
              @for (hour of hours; track hour.id) {
                <div class="capacity-stage__hour-row">
                  <lib-fluid-capacity-hour
                    [hourLabel]="hour.hourLabel"
                    [minHeight]="76"
                    [slots]="hour.slots"
                    [carryState]="hour.carryState"
                    geometryMode="level"
                    renderVariant="capacity-intelligence-maturity"
                    (carryStateChange)="onCarryStateChanged(hour.id, $event)"
                  />
                </div>
              }
            </div>
          </div>
        }
      </section>
    }
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      min-width: 0;
    }

    .capacity-stage {
      display: grid;
      gap: 16px;
      min-width: 0;
    }

    .capacity-stage__toolbar {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      align-items: end;
      padding: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #ffffff;
      min-width: 0;
    }

    .capacity-stage__field {
      display: grid;
      gap: 6px;
      min-width: min(260px, 100%);
      flex: 1 1 260px;
      font-size: 13px;
      color: #374151;
    }

    .capacity-stage__field select {
      min-height: 40px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 0 12px;
      background: #ffffff;
      color: #111827;
    }

    .capacity-stage__refresh {
      min-height: 40px;
      border: 1px solid #2563eb;
      border-radius: 8px;
      padding: 0 14px;
      background: #2563eb;
      color: #ffffff;
      font-weight: 600;
      cursor: pointer;
    }

    .capacity-stage__refresh:disabled {
      cursor: not-allowed;
      border-color: #cbd5e1;
      background: #e5e7eb;
      color: #64748b;
    }

    .capacity-stage__day {
      display: grid;
      gap: 16px;
      min-width: 0;
      overflow: hidden;
      padding: 16px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: #ffffff;
    }

    .capacity-stage__day-head {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      align-items: start;
      min-width: 0;
    }

    .capacity-stage__day-head h3 {
      margin: 0;
      color: #111827;
      font-size: 16px;
    }

    .capacity-stage__day-head p {
      margin: 4px 0 0;
      color: #6b7280;
      font-size: 13px;
    }

    .capacity-stage__hours {
      display: grid;
      gap: 8px;
      min-width: 0;
      overflow: hidden;
    }

    .capacity-stage__hour-row {
      min-width: 0;
    }

    @media (max-width: 760px) {
      .capacity-stage__toolbar {
        display: grid;
      }

      .capacity-stage__field {
        min-width: 0;
        width: 100%;
      }

      .capacity-stage__refresh {
        justify-self: start;
      }

      .capacity-stage__day {
        gap: 12px;
        padding: 8px 6px;
      }

      .capacity-stage__day-head p {
        max-width: 32ch;
      }

      .capacity-stage__hour-row {
        min-width: 0;
      }
    }
  `],
})
export class AppointmentCapacityStagePageComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly settingsService = inject(SettingsService);
  private readonly slotsService = inject(SlotsService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly subscriptions = new Subscription();

  protected clinicOptions: Array<{ id: string; label: string; timeZone: string }> = [];
  protected selectedClinicId = '';
  protected selectedDate = this.todayDateKey();
  protected hours: CapacityStageHour[] = [];
  protected isLoading = false;
  protected errorText = '';
  protected companyContextMissing = false;

  private readonly isSystemAdmin = this.authService.getCurrentRole() === 'system_admin';

  public ngOnInit(): void {
    this.selectedClinicId = this.systemAdminContext.getClinicId()?.trim() ?? '';
    this.refreshCompanyContextState();
    this.loadClinicOptions();

    if (this.isSystemAdmin) {
      this.subscriptions.add(this.systemAdminContext.companyId$.subscribe(() => {
        this.selectedClinicId = this.systemAdminContext.getClinicId()?.trim() ?? '';
        this.refreshCompanyContextState();
        this.loadClinicOptions();
      }));
      this.subscriptions.add(this.systemAdminContext.clinicId$.subscribe((clinicId) => {
        this.selectedClinicId = clinicId?.trim() ?? '';
        this.loadCapacityStage();
      }));
      this.subscriptions.add(this.systemAdminContext.scopeMode$.subscribe(() => {
        this.refreshCompanyContextState();
      }));
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected onDateChanged(value: string): void {
    this.selectedDate = value;
    this.loadCapacityStage();
  }

  protected onClinicChanged(value: string): void {
    this.selectedClinicId = value?.trim() ?? '';
    this.loadCapacityStage();
  }

  protected loadCapacityStage(): void {
    this.errorText = '';
    this.hours = [];

    if (this.companyContextMissing || !this.selectedClinicId) {
      return;
    }

    this.isLoading = true;
    this.slotsService.getCapacityIntelligence({
      clinicId: this.selectedClinicId,
      schedulingDate: this.selectedDate,
    }).pipe(
      catchError((error: unknown) => {
        console.error('Failed to load capacity stage', error);
        this.errorText = 'Capacity intelligence read model is unavailable for the selected scope.';
        return of({ items: [] });
      }),
      finalize(() => {
        this.isLoading = false;
      }),
    ).subscribe((response) => {
      this.hours = this.buildHours(response.items ?? []);
    });
  }

  protected onCarryStateChanged(hourId: string, value: FluidCapacityCarryState): void {
    this.hours = this.hours.map((hour) => hour.id === hourId
      ? { ...hour, carryState: value }
      : hour);
  }

  private loadClinicOptions(): void {
    this.clinicOptions = [];

    if (this.companyContextMissing) {
      return;
    }

    this.settingsService.getClinicOperationalSettings().subscribe({
      next: (items: ClinicOperationalSettingsDto[]) => {
        this.clinicOptions = items.map((item) => ({
          id: item.clinicId,
          label: item.clinicName,
          timeZone: item.timeZone || item.timeZoneInherited || 'UTC',
        }));

        if (!this.selectedClinicId && this.clinicOptions.length === 1) {
          this.selectedClinicId = this.clinicOptions[0].id;
        }

        this.loadCapacityStage();
      },
      error: (error: unknown) => {
        console.error('Failed to load clinics for capacity stage', error);
        this.errorText = 'Clinic options are unavailable for the current scope.';
      },
    });
  }

  private refreshCompanyContextState(): void {
    this.companyContextMissing =
      this.isSystemAdmin
      && (
        this.systemAdminContext.isGlobalScope()
        || !this.systemAdminContext.getCompanyId()?.trim()
      );
  }

  private buildHours(items: SlotCapacityIntelligenceWindowDto[]): CapacityStageHour[] {
    const groups = new Map<string, SlotCapacityIntelligenceWindowDto[]>();

    for (const item of items) {
      const key = this.hourKey(item.startTime, item.clinicTimeZone);
      groups.set(key, [...(groups.get(key) ?? []), item]);
    }

    return Array.from(groups.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, group]) => ({
        id: key,
        hourLabel: `${key.slice(11, 13)}:`,
        slots: group
          .sort((left, right) => new Date(left.startTime).getTime() - new Date(right.startTime).getTime())
          .map((item) => this.toHourSlot(item)),
        carryState: {
          overflowSlotId: null,
          overflowMinutes: 0,
        },
      }));
  }

  private toHourSlot(item: SlotCapacityIntelligenceWindowDto): FluidCapacityHourSlot {
    const timeZone = item.clinicTimeZone || 'UTC';
    const startParts = this.timeParts(item.startTime, timeZone);
    const durationMinutes = Math.max(1, Math.round(
      (new Date(item.endTime).getTime() - new Date(item.startTime).getTime()) / 60000,
    ));

    return {
      id: item.id,
      startMinute: startParts.minute,
      durationMinutes,
      startLabel: this.timeLabel(item.startTime, timeZone),
      endLabel: this.timeLabel(item.endTime, timeZone),
      specialistAvailable: item.specialist.available,
      specialistTotal: item.specialist.total,
      deviceAvailable: item.device.available,
      deviceTotal: item.device.total,
      status: 'available',
      intelligence: {
        signal: item.signal,
        label: this.signalLabel(item.signal),
        followUp: this.reasonLabel(item.reasonCode),
        deviceRequiredTimePercent: item.device.total > 0 ? 100 : 0,
        specialistIdlePercent: Math.round((1 - item.specialist.utilization) * 100),
        deviceIdlePercent: Math.round((1 - item.device.utilization) * 100),
        preferenceSkewPercent: item.signal === 'preference-skew'
          ? Math.round(item.resourceMismatchScore * 100)
          : 0,
        businessSuccessScore: item.businessSuccessScore,
        effectiveThroughputScore: item.effectiveThroughputScore,
      },
    };
  }

  private hourKey(value: string, timeZone: string): string {
    const parts = this.timeParts(value, timeZone);
    return `${parts.date}T${String(parts.hour).padStart(2, '0')}`;
  }

  private timeParts(value: string, timeZone: string): { date: string; hour: number; minute: number } {
    const parts = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone,
    }).formatToParts(new Date(value));
    const read = (type: string) => parts.find((part) => part.type === type)?.value ?? '00';

    return {
      date: `${read('year')}-${read('month')}-${read('day')}`,
      hour: Number(read('hour')),
      minute: Number(read('minute')),
    };
  }

  private timeLabel(value: string, timeZone: string): string {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone,
    }).format(new Date(value));
  }

  private signalLabel(signal: SlotCapacitySignal): string {
    switch (signal) {
      case 'expand':
        return 'Expand';
      case 'hire-specialists':
        return 'Need S';
      case 'add-devices':
        return 'Need D';
      case 'mix-waste':
        return 'Mix waste';
      case 'preference-skew':
        return 'New S idle';
      case 'blocked':
        return 'Blocked';
      case 'healthy':
      default:
        return 'Healthy';
    }
  }

  private reasonLabel(reason: SlotCapacityReasonCode): string {
    return reason;
  }

  private todayDateKey(): string {
    return new Date().toISOString().slice(0, 10);
  }
}
